import { User } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { EmptyState, LoadingSpinner, PatternBackground, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { resolveChipState } from './chipState';
import { createStyles } from './SearchUsersScreen.styles';

export interface SearchUsersScreenProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearch: (query: string) => void;
  results: User[];
  loading: boolean;
  loadingMore: boolean;
  currentUserId?: string;
  followingIds: Set<string>;
  friendIds: Set<string>;
  pendingSentIds: Set<string>;
  onLoadMore: () => void;
  onFollowToggle: (userId: string, isFollowing: boolean) => void;
  onAddFriend: (userId: string) => void;
  onUserPress: (userId: string) => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const SearchUsersScreen = ({
  searchQuery,
  onSearchChange,
  onSearch,
  results,
  loading,
  loadingMore,
  currentUserId,
  followingIds,
  friendIds,
  pendingSentIds,
  onLoadMore,
  onFollowToggle,
  onAddFriend,
  onUserPress,
  colors,
}: SearchUsersScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderUserItem = ({ item }: { item: User }) => {
    const isCurrentUser = item.id === currentUserId;
    const isFollowing = followingIds.has(item.id);
    const isFriend = friendIds.has(item.id);
    const isPendingSent = pendingSentIds.has(item.id);
    const chips = resolveChipState(isCurrentUser, isFriend, isPendingSent);

    return (
      <UserCard
        userId={item.id}
        userName={item.name}
        userEmail={item.email}
        userAvatarUrl={item.avatarUrl}
        onPress={() => onUserPress(item.id)}
        actions={
          !chips.showNone ? (
            <View style={styles.chips}>
              {chips.showFriendBadge ? (
                <View style={[styles.chip, { borderColor: '#22C55E' }]}>
                  <Ionicons name="checkmark" size={11} color="#22C55E" />
                  <Text style={[styles.chipText, { color: '#22C55E' }]}>{t('social.friends')}</Text>
                </View>
              ) : (
                <>
                  {chips.showFollowChip && (
                    <TouchableOpacity
                      style={[
                        styles.chip,
                        isFollowing
                          ? { borderColor: colors.border }
                          : { backgroundColor: colors.primary, borderColor: colors.primary },
                      ]}
                      onPress={() => onFollowToggle(item.id, isFollowing)}
                    >
                      <Text style={[styles.chipText, { color: isFollowing ? colors.textSecondary : colors.primaryText }]}>
                        {isFollowing ? t('social.following') : t('social.follow')}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {chips.showPending && (
                    <View style={[styles.chip, { borderColor: colors.border }]}>
                      <Text style={[styles.chipText, { color: colors.textSecondary }]}>{t('social.pending')}</Text>
                    </View>
                  )}
                  {chips.showAddFriend && (
                    <TouchableOpacity
                      style={[styles.chip, { borderColor: colors.border }]}
                      onPress={() => onAddFriend(item.id)}
                    >
                      <Ionicons name="person-add-outline" size={13} color={colors.primary} />
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>
          ) : null
        }
        colors={colors}
      />
    );
  };

  return (
    <PatternBackground>
      <View style={[styles.searchContainer, { backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }]}>
        <View style={[styles.searchBar, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Ionicons name="search" size={20} color={colors.textSecondary} />
          <TextInput
            style={[styles.searchInput, { color: colors.text }]}
            placeholder={t('search.searchPlaceholder')}
            placeholderTextColor={colors.textTertiary}
            value={searchQuery}
            onChangeText={onSearchChange}
            onSubmitEditing={() => onSearch(searchQuery)}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => onSearchChange('')}>
              <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {loading ? (
        <LoadingSpinner color={colors.primary} backgroundColor={colors.background} />
      ) : (
        <FlatList
          data={results}
          renderItem={renderUserItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.5}
          ListFooterComponent={loadingMore ? <LoadingSpinner color={colors.primary} backgroundColor={colors.background} /> : null}
          ListEmptyComponent={
            searchQuery.length >= 2 ? (
              <EmptyState
                icon={<Ionicons name="search-outline" size={52} color={colors.textTertiary} />}
                title={t('search.noUsersFound')}
                subtitle={t('search.tryDifferentSearch')}
                colors={colors}
              />
            ) : (
              <EmptyState
                icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
                title={t('search.searchForUsers')}
                subtitle={t('search.minCharacters')}
                colors={colors}
              />
            )
          }
        />
      )}
    </PatternBackground>
  );
};
